import _ from 'lodash';
import { nanoid as nanoid }  from 'nanoid';
window.pageToken = nanoid(8);

import { LOCALES, OPTIONS, ERROR, CATEGORY } from '../../../constants';
import FormatterUtils from '../../../routes/utils/formatter-util';
import * as _LocaleUtils from '../../../routes/utils/locale-util';
import OptUtils from '../../../routes/utils/option-util';
import MathUtils from '../../../routes/utils/math-util';
import { LocalStorage as ls } from './Cache';

Constants.J104.Categories = CATEGORY;
Constants.Locales = LOCALES;
Constants.Message = {
	ERROR,
};
// Constants.J104.Box.Confirm = {
// 	yesButton: '是',
// 	noButton: '否',
// };

window.AuthUtils = Object.freeze({
	PERMISSION: Object.freeze({
		IS_QUERY: 1,
		IS_PRINT: 2,
		IS_EDIT: 4,
		IS_MGT: 8,
	}),

	/**
	 * <p>回傳一個 chkUserPrivilege 用的 function</p>
	 * <p>如果是「Admin使用者」直接回傳 true</p>
	 * <p>否則只要符合 needPrivileges 其中一項權限，該 function 就會回傳 true</p>
	 * <p>(跟後端 privilegeChecker.js 的 oneOf 完全相同)</p>
	 * @param {needPrivileges} needPrivileges
	 * @return {function(userPrivileges, User): boolean}
	 */
	oneOf: (needPrivileges) => {
		return (userPrivileges, user) => {
			if (user.identity === 0) return true;
			return _.some(needPrivileges, (permission, code) => {
				return _.isFunction(permission) ? permission(userPrivileges, user) : !!(userPrivileges[code] & permission);
			});
		};
	},

	/**
	 * <p>回傳一個 chkUserPrivilege 用的 function</p>
	 * <p>如果是「Admin使用者」直接回傳 true</p>
	 * <p>否則需要符合 needPrivileges 之中所有權限，該 function 才會回傳 true</p>
	 * <p>(跟後端 privilegeChecker.js 的 allOf 完全相同)</p>
	 * @param {needPrivileges} needPrivileges
	 * @return {function(userPrivileges, User): boolean}
	 */
	allOf: (needPrivileges) => {
		return (userPrivileges, user) => {
			if (user.identity === 0) return true;
			return _.every(needPrivileges, (permission, code) => {
				return _.isFunction(permission) ? permission(userPrivileges, user) : !!(userPrivileges[code] & permission);
			});
		};
	},

	/**
	 * <p>回傳一個 chkUserPrivilege 用的 function</p>
	 * <p>回傳值為 fn 的反向邏輯</p>
	 * <p>(跟後端 privilegeChecker.js 的 not 完全相同)</p>
	 * @param {function(userPrivileges, User): boolean} fn
	 * @return {function(userPrivileges, User): boolean}
	 */
	not: (fn) => {
		return (userPrivileges, user) => {
			return !fn(userPrivileges, user);
		};
	},

	/**
	 * <p>回傳一個 chkUserPrivilege 用的 function</p>
	 * <p>不允許任何豁免，需要符合 needPrivileges 之中所有權限，該 function 才會回傳 true</p>
	 * <p>(跟後端 privilegeChecker.js 的 strictAllOf 完全相同)</p>
	 * @param {needPrivileges} needPrivileges
	 * @return {function(userPrivileges, User): boolean}
	 */
	strictAllOf: (needPrivileges) => {
		return (userPrivileges, user) => {
			return _.every(needPrivileges, (permission, code) => {
				return _.isFunction(permission) ? permission(userPrivileges, user) : !!(userPrivileges[code] & permission);
			});
		};
	},

	/**
	 * <p>回傳一個 chkUserPrivilege 用的 function</p>
	 * <p>必須是「Admin使用者」，並符合額外參數(有的話)</p>
	 * <p>(跟後端 privilegeChecker.js 的 isAdmin 完全相同)</p>
	 * @param {Object} opt
	 * @param {boolean} opt.isMgtScope1 是否為全集團? undefined:不重要; true:必須是全集團; false:不能是全集團
	 * @return {function(userPrivileges, User): boolean}
	 */
	isAdmin: (opt) => {
		return (userPrivileges, user) => {
			if (user.identity !== 0) return false;
			if (!_.isUndefined(opt?.isMgtScope1))
				return opt.isMgtScope1 && user.mgtScope === 1
					|| !opt.isMgtScope1 && user.mgtScope !== 1;
			return true;
		};
	},

	/**
	 * <p>判斷是否是 administrator</p>
	 * <p>注意，這跟使用者身分裡的「Admin使用者」是不一樣的東西</p>
	 * <p>(跟後端 privilegeChecker.js 的 isAdmin 完全相同)</p>
	 * @param userPrivileges
	 * @param {User} user
	 * @return {boolean}
	 */
	isAdministrator: (userPrivileges, user) => {
		return user.id === 1;
	},

	/**
	 * <p>判斷是否開啟 104 雲服務</p>
	 * <p>(跟後端 privilegeChecker.js 的 is104CloudService 完全相同)</p>
	 * @param userPrivileges
	 * @param {User}  user
	 * @return {boolean}
	 */
	is104CloudService: (userPrivileges, user) => {
		return user.isCloudService;
	},

	/**
	 * <p>判斷是否具備員工身分</p>
	 * <p>(跟後端 privilegeChecker.js 的 isEmp 完全相同)</p>
	 * @param userPrivileges
	 * @param {User} user
	 * @return {boolean}
	 */
	isEmp: (userPrivileges, user) => {
		return user.isEmp === 1;
	},

	/**
	 * <p>(跟後端 privilegeChecker.js 的 chkUserPrivilege 完全相同)</p>
	 * @param user
	 * @param {function(*, User): boolean} fn oneOf, allOf 或 isAdmin 回傳的 function
	 * @return {boolean}
	 */
	chkUserPrivilege: (user, fn) => {
		if (!user) return false;
		const { bid, privileges } = user;
		return fn(privileges[bid] || privileges[0], user); // 標準使用者
	},

	/**
	 * 前端的 user 已經綁到全域變數裡，可以不用手動傳 user 給 chkUserPrivilege
	 * @param {function(*): boolean} fn oneOf 或 allOf 回傳的 function
	 * @return {boolean}
	 */
	chkPrivilege: (fn) => AuthUtils.chkUserPrivilege(window.user, fn),

	/**
	 * 可以的話，加密文字
	 * @param {string} plain
	 * @return {string|null} cipher or plain
	 */
	encrypt: (plain) => {
		if (!window.encPwd) return plain;
		try {
			const buf = Buffer.from(window.__pub__, 'base64');
			const pubKey = forge.pki.publicKeyFromPem(buf);
			const encrypted = pubKey.encrypt(forge.util.encodeUtf8(plain), 'RSA-OAEP', {md: forge.md.sha256.create()});
			return forge.util.encode64(encrypted);
		} catch (err) {
			console.error(err);
			Utils.noticeError(null, `加密失敗: ${err.message}`);
			return null;
		}
	},
});
window.LocaleUtils = {
	locales: LOCALES,
	i18nIdx: (user) => _LocaleUtils.i18nIdx(user || window.user),
	i18n: (obj, user) => _LocaleUtils.i18n(obj, user || window.user),
	adapt: (obj, user) => _LocaleUtils.adapt(obj, user || window.user),
	i18nKey: () => {
		return _LocaleUtils.allI18n()[_LocaleUtils.i18nIdx(window.user)];
	},
	allI18n: _LocaleUtils.allI18n
};
window.OptionUtils = {
	... OptUtils,
	get: OptUtils.getOptions, // rename
	getOptions: key => OptUtils.getLocaledOptions(key, LocaleUtils.i18nIdx()),
	getTreeOptions: key => OptUtils.getTreeOptions(key, LocaleUtils.i18nIdx()),
	getUserOptions: cacheKey => ls.get(cacheKey),
	getLabel: (key, value, replacement, localeIdx) => {
		let _localeIdx = localeIdx !== undefined ? localeIdx : LocaleUtils.i18nIdx();
		if (typeof replacement === 'number') {
			_localeIdx = replacement;
		} else if (typeof replacement === 'object' && replacement !== null) {
			return OptUtils.getLabelWithTemplate(key, value, _localeIdx, replacement);
		}
		return OptUtils.getLabel(key, value, _localeIdx);
	},
	getJoinedLabels: (key, values, delimiter) => OptUtils.getJoinedLabels(key, values, LocaleUtils.i18nIdx(), delimiter),
	fixDisable: (options, excludeIds) => OptUtils.fixDisable(options, excludeIds, LocaleUtils.i18nIdx()),
	adaptDisable: (options) => OptUtils.adaptDisable(options, LocaleUtils.i18nIdx()),
	getWeekDayByNum: (num) => {
		return LocaleUtils.i18nIdx() === 1 ? OptionUtils.getLabel('global.week', num) : OptionUtils.getLabel('global.week', num).slice(-1);
	},
	/**
	 * 靜態代碼檔, 但只/不能使用指定項目
	 * @param {string} code 代碼
	 * @param {*} maps 物件 map 指定項目 {id:1, ...} 或 [id, ...]
	 * @param {boolean} rev 反向(排除) map 項目
	 * @returns {array} 一律轉陣列 [{id, name}, ...]
	 */
	pickOpt: (code, maps, rev) => {
		let list = OptionUtils.getOptions(code);
		if (!Array.isArray(list)) list = Object.keys(list).map(id => ({ id, name: list[id] }));
		if (Array.isArray(maps)) maps = maps.reduce((prev, prop) => ({ ...prev, [prop]: 1 }), {});

		return list.reduce((previousValue, currentValue) => {
			if (rev ? !maps[currentValue.id] : maps[currentValue.id]) previousValue.push(currentValue);

			return previousValue;
		}, []);
	},
};
window.CategoryUtils = {
	getLabel: code => {
		var _findChildren = function(children, code, degree){
			var find = '';
			children.each(function(child){
				if(child.value == (code / degree).toInt() * degree)
					find = (code % degree === 0) ? child.text : child.children ? _findChildren(child.children, code, degree/1000) : '';
			});
			return find;
		};

		if(!code) return '';
		if(typeOf(code) === 'array'){
			var cates = '';
			code.each(function(v, idx){ cates += CategoryUtils.getLabel(v) + (idx < code.length - 1 ? ', ' : '');});
			return cates;
		}
		code = code.toInt();
		var c = Constants.J104.Categories[(code / 1000000000).toInt() + '000000000'];
		if(!c) return '';
		return _findChildren(c, code, 1000000);
	},
};
window.Utils = {
	Format: {
		...FormatterUtils,
		/**
		 * @param {*} src 任何 moment 能 parse 的東西
		 * @param {boolean} [isSecond] 回傳值是否包含 "秒"
		 * @returns {string} YYYY/MM/DD HH:mm:ss 格式的字串
		 */
		coDateTime: (src, isSecond) => `${FormatterUtils.dateTime(src, isSecond, Utils.coTimeZone())} (${Utils.coTimeZone()})`,
	},
	notice: (target, msg, type, options) => {
		if(!target) target = document.body;
		let stick;
		if(options && options.stick){
			stick = options.stick;
			delete options.stick;
		}
		let _options = {
			single: false,
			stick: {
				target,
				position: 'bottomRight',
				edge: 'bottomRight',
				offset: { x: -20, y: -20 },
				... stick,
			},
			level: 999,
			... options,
		};
		if(!_.includes(['success', 'info', 'warning', 'error'], type)) type = 'notice';
		// 容器定義在 J104.Box.js
		return target[type](msg, _options);
	},
	noticeSuccess: (target, msg, options) => {
		Utils.notice(target, msg, 'success', options);
	},
	noticeInfo: (target, msg, options) => {
		Utils.notice(target, msg, 'info', options);
	},
	noticeWarning: (target, msg, options) => {
		Utils.notice(target, msg, 'warning', options);
	},
	noticeError: (target, msg, options) => {
		Utils.notice(target, msg, 'error', {
			stick: {
				position: 'top',
				edge: 'top',
				offset: { x: 0, y: 20 },
			},
			... options,
		});
	},
	cancelAjax: src => src.isRunning ?
		(src.isRunning() ? src.cancel() : null) :
		_.forEach(src, a => a && a.isRunning() ? a.cancel() : null),
	binaryLabels: (dataSet, value) => {
		if (_.isNil(value)) return '';
		if (!_.isArray(dataSet)) {
			dataSet = OptionUtils.getOptions(dataSet);
			if (!dataSet) return '';
			dataSet = _.values(dataSet);
		}
		let str = '';
		(value).toString(2).split('').reverse().each((c, idx) => {
			if (c === '0' || !dataSet[idx]) return;
			str += (str.length === 0 ? '' : ', ') + dataSet[idx];
		});
		return str;
	},


	// 以下待整理!
	fixDate: value => {
		return (value === '0000-00-00' || value === '9999-12-31') ? '' : value;
	},
	fixEmpty: value => {
		return value === '' ? 'N/A' : value;
	},

	/**
	 * 取得公司時區 (例如 'Asia/Taipei')
	 * @returns {string|null}
	 */
	coTimeZone: () => {
		try{
			let { companies, bid } = window.user;
			let company = _.find(companies, { id: bid });
			return company ? company.timeZone : null;
		}
		catch(err){return null;}
	},

	formatCurrency: (value, code) => {
		console.warn('[Legacy Method] "formatCurrency()" will be removed soon! Use Utils.Format.currency() instead.');
		if (_.isNil(value)) return '';
		const CURRENCIES = {
			TWD: { prefix: 'NT$', decimals: 0 },
			USD: { prefix: '$', decimals: 2 },
			EUR: { prefix: '€', decimals: 2 },
			CNY: { prefix: '¥', decimals: 2 },
			HKD: { prefix: '$', decimals: 2 },
			JPY: { prefix: '¥', decimals: 0 },
			THB: { prefix: '฿', decimals: 0 },
			IDR: { prefix: '₨', decimals: 0 },
			VND: { prefix: '₫', decimals: 0 },
		};
		if (!_.includes(_.keys(CURRENCIES), code.toUpperCase())) code = 'TWD';
		return (value * 1).format(CURRENCIES[code]);
	},
	dateTime: (src) => {
		console.warn('[Legacy Method] "dateTime()" will be removed soon! Use Utils.Format.dateTime() instead.');

		if (!src || src === '0000-00-00' || src === '9999-99-99') return '';
		return moment(src).format('YYYY/MM/DD HH:mm:ss');
	},
	i18n: (src, type = 1) => {
		console.warn('[Legacy Method] "i18n()" will be removed soon! Use LocaleUtils.i18n() instead.');

		// let localeKey = 'zh_TW';
		// if (user && user.locale) localeKey = user.locale;
		// return src ? src[localeKey] : '';
		if (!src) {
			return src;
		}
		let localeKey = 'zh_TW';
		if (window.user && window.user.locale) localeKey = window.user.locale;
		let name = src[localeKey];
		//應用
		//1.screen.login 語系 值
		//2.公司語系 值
		//3.依照語言順序 , 取第1個有的值
		//管理 screen.login 語系 值
		if (type === 1) {	//應用
			let coLocale = Utils.coLocale();
			if (name === '' && src.hasOwnProperty(coLocale)) name = src[coLocale];		//公司語系
			if (name === '') {
				//排除公司語系
				let otherLocale = _.omit(LOCALES, [localeKey, coLocale]);
				//取其他不為空值的語系
				for (let property in otherLocale) {
					if (src.hasOwnProperty(property) && src[property] !== '') {
						name = src[property];
						break;
					}
				}
			}
		}
		return name;
	},
	adaptI18n: (src) => {
		console.warn('[Legacy Method] "adaptI18n()" will be removed soon! Use LocaleUtils.adapt() instead.');
		if (!_.isObject(src)) return src;
		let localeKey = 'zh_TW';
		let clone = _.clone(src);
		const user = wwindow.user;
		if (user) {
			localeKey = user.locale;
			if (src.hasOwnProperty(localeKey) && !_.isEmpty(src[localeKey])) return src[localeKey];
			else clone = _.omit(clone, localeKey);
			if (user.companies && user.bid) {
				let c = _.find(user.companies, { id: user.bid });
				if (c) {
					localeKey = c.locale;
					if (src.hasOwnProperty(localeKey) && !_.isEmpty(src[localeKey])) return src[localeKey];
					else clone = _.omit(clone, localeKey);
				}
			}
		}
		let values = _.values(_.pickBy(clone));
		return (values && values.length > 0) ? values[0] : '';
	},
	//取得公司語系
	coLocale: () => {
		console.warn('[Legacy Method] "coLocale()" will be removed soon!!');
		let { companies, bid } = window.user;
		let company = _.find(companies, { id: bid });
		return company ? company.locale : null;
	},
	i18nIdx: () => {	// !!! remove later, LocaleUtils.i18nIdx() instead
		console.warn('[Legacy Method] "i18nIdx()" will be removed soon! Use LocaleUtils.i18nIdx() instead.');
		_.indexOf(_.keys(Constants.Locales), window.user.locale) || 0;
	},
	options: (key) => {														// !!! remove later, OptionUtils.getOptions() instead
		console.warn('[Legacy Method] "options()" will be removed soon! Use OptionUtils.getOptions() instead.');
		const src = OPTIONS;
		if (!src.hasOwnProperty(key)) return null;
		return _.transform(src[key], (result, value, key) => result[key] = value[LocaleUtils.i18nIdx()]);
	},
	optionLabel: (key, value) => {											// !!! remove later, OptionUtils.getLabel() instead
		console.warn('[Legacy Method] "optionLabel()" will be removed soon! Use OptionUtils.getLabel() instead.');
		const src = OPTIONS;
		if (!src.hasOwnProperty(key)) return 'N/A';
		const label = src[key][value];
		if (!label) return 'N/A';
		return label[LocaleUtils.i18nIdx()];
	},
	optionLabelArr: (key, value) => {
		console.warn('[Legacy Method] "optionLabelArr()" will be removed soon! Use OptionUtils.getJoinedLabels() instead.');
		// console.log('optionLabelArr|value|', value);
		let input = _.isArray(value) ? value
			: (value && value.length > 0) ? value.split(',') : value;
		if (!_.isArray(input)) return Utils.optionLabel(key, value);
		let out = [];
		// console.log('optionLabelArr|input|', input);
		_.forEach(input, item => {
			let optionValue = Utils.optionLabel(key, item);
			// console.log('optionLabelArr|optionValue|', optionValue);
			out.push(optionValue);
			// console.log('optionLabelArr|out|', out);
		});
		if (out.length === 0) return 'N/A';
		return out.join(',');
	},
	downloadUrl: (uuid) => {
		return `api/file/download/${uuid}`;
	},
	imgUrl: (uuid) => {
		return `api/file/img/${uuid}`;
	},
	/**
	 * 泛用多元件寫值
	 * @param {*} inputs 元件的集合
	 * @param {*} values 值資料
	 * @param {boolean} fireEvent 傳遞事件
	 */
	writeVal: (inputs, values, fireEvent) => Object.keys(values || 0).forEach(value => {
		const input = inputs[value];
		if (input && input.setValue) input.setValue(values[value], fireEvent);
	}, {}),
	/**
	 * 泛用多元件自動復位
	 * @param {*} inputs 元件的集合
	 * @param {function} exFunc 例外處理
	 */
	resetVal: (inputs, exFunc) => Object.values(inputs).forEach(value => {
		if (!value || !value.reset) return;
		try {
			value.reset();
		}
		catch (ex) {
			(exFunc || (() => { }))(ex, value);
		}
	}),
	/**
	 * 泛用多元件自動驗證
	 * @param {*} inputs 元件的集合
	 * @returns {boolean} 驗證結果
	 */
	checkVal: inputs => Object.values(inputs).reduce((prev, currentValue) => {
		if (currentValue && currentValue.validate) {
			const v = currentValue.validate();
			// if (!v) console.log(currentValue);
			return v && prev;
		} else return prev;
	}, true),
	/**
	 * 泛用多元件取值
	 * @param {*} inputs 元件的集合
	 * @returns {*} 收集元件值
	 */
	readVal: inputs => Object.keys(inputs).reduce((previousValue, currentValue) => {
		const input = inputs[currentValue];
		if (input && input.getValue) previousValue[currentValue] = input.getValue();

		return previousValue;
	}, {}),
	/**
	 * 泛用多元件(不)啟用
	 * @param {*} inputs 元件的集合
	 * @param {boolean} enable 啟用與否
	 */
	wakeVal: (inputs, enable = true) => Object.values(inputs).forEach(value => {
		const func = enable === false ? 'disable' : 'enable';
		if (value && value[func]) value[func]();
	}),
	/**
	 * 泛用多元件(不)鎖定
	 * @param {*} inputs 元件的集合
	 * @param {boolean} editable 解鎖與否
	 */
	freeVal: (inputs, editable = true) => Object.values(inputs).forEach(value => {
		const func = editable === false ? 'readOnly' : 'editable';
		if (value && value[func]) value[func]();
	}),
	/**
	 * 產生訊息的 function
	 * @callback block~promotMessage
	 * @return {undefined|string} 回傳 undefined (或無回傳值) 視為不需攔截，其他東西強制轉成字串做為要顯示的訊息
	 */
	/**
	 * Attention: 如果只是要刪除暫存資料，請試著使用下面的 Utils.blockBeforeUnload(arg)<br/>
	 * --<br/>
	 * 阻擋使用者切換頁面，要解除的話請呼叫此 function 回傳的 function。
	 * 由於瀏覽器的限制，透過重整頁面、輸入網址等由 beforeunload 事件攔截的操作，無法指定攔截訊息。
	 * @param {string|block~promotMessage} arg 要顯示的訊息(如果可以顯示的話)，任何非 function 的值都會強制轉成字串處理
	 * @return {function} 解除 block 時要執行的 function
	 * @see https://github.com/ReactTraining/history/blob/master/docs/Blocking.md
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
	 */
	block: (arg) => {
		const isFunc = _.isFunction(arg);
		if (_.isFunction(Utils.$unblock)) Utils.$unblock();
		let unblock;
		if (Utils.$history) {
			unblock = Utils.$history.block(!isFunc ? String(arg) : (location, action) => {
				const ret = arg();
				return ret === undefined ? ret : String(ret);
			});
		}

		const beforeunloadListener = (e) => {
			if (isFunc && arg() === undefined)
				return;

			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', beforeunloadListener);

		Utils.$unblock = () => {
			if (unblock) unblock();
			window.removeEventListener('beforeunload', beforeunloadListener);
		};
		return Utils.$unblock;
	},
	/**
	 * 阻擋使用者重新整理或關閉分頁。要解除的話請呼叫此 function 回傳的 function。<br/>
	 * 通常，如果只是要刪除暫存資料，使用這個 utility 跟 componentWillUnmount 就可以達成。
	 * @param {string|block~promotMessage} arg 任何非 function 的值，或是 function 回傳值非 undefined 都會造成阻止導頁
	 * @return {function} 解除 block 時要執行的 function
	 */
	blockBeforeUnload: (arg) => {
		const beforeunloadListener = (e) => {
			if (_.isFunction(arg) && arg() === undefined)
				return;

			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', beforeunloadListener);

		Utils.$unblock = () => {
			window.removeEventListener('beforeunload', beforeunloadListener);
		};
		return Utils.$unblock;
	},
	/**
	 * 回傳輸入分鐘、小時、天單位數的 RNumber.props.validate 物件 (min, max, required)
	 * 分鐘: 最大值 999999999
	 * 小時、天: 最大值 999999999.9
	 * @param {string} unit 單位字串 ('minute', 'hour', 'day')
	 * @param {boolean} required true: 必填; false: 非必填
	 * @return {obj}} validate 物件
	 */
	 validatorForUnit: (unit, required, max, min) => {
		return {
			min: min === undefined ? unit === 'minute' ? 1 : 0.1 : min,
			max: max === undefined ? unit === 'minute' ? 999999999 : 999999999.9 : max,
			required,
		};
	},
	reloadByUserTool: null,	// 用來掛生效作業後重載資料的 function
};
window.MathUtils = {
	...MathUtils,
};

const _appErrorHandler = (status, xhr) => {
	const handler = window.APP.appError || window.APP.devError;
	if (handler && _.includes([404, 440, 499, 500], status))
		handler({
			xhr,
			msg: {
				404: '找不到位置啦！',
				440: '搞什麼, 參數錯誤啦!!',
				499: 'DB 錯誤',
				500: '後端掛了，快去檢查!!',
			}[status],
		});
};
const _defaultErrorHandler = (self, response, event = true) => {
	const {errMsg, errCode, loadErrMsg, useErrMsg = true, loadErrCode, noticeContainer: nc, noticeOptions: no} = self.options;
	let msg = errMsg || loadErrMsg || (errMsg === undefined && loadErrMsg === undefined ? OptionUtils.getLabel('global.normal','error') : '');
	if (!useErrMsg) msg = '';
	const code = errCode || loadErrCode;
	const {status} = self.xhr;
	if (status === 401) {
		if (!window.__reloading) {
			window.__reloading = true;
			alert(OptionUtils.getLabel('hp.RouteWrapper', 'loginFailedTheAccountStatus') || '系統已自動登出，請重新登入');
			window.location.reload();
		}
		return;
	}
	if (status === 403 && response?.csrf === false) {
		if (!window.__reloading) {
			window.__reloading = true;
			window.location.reload();
		}
		return;
	}
	let extraMsg = {
		...window.APP.appError ? {
			[status]: '抱歉！網頁暫時出狀況，請您稍候再試',
			0: '無法與伺服器建立連線，請檢查您的網路環境後再試一次',
			403: OptionUtils.getLabel('hp.RouteWrapper', 'verifyOfYourAccount'),
			404: '抱歉！您要找的頁面已不存在',
			409: 'user.active 為 false (鎖定頁面 bug 追蹤訊息)',
			489: '檔案過舊已清除。請重新下載', // 下載檔案錯誤
		} : Constants.Message.ERROR,
		... _.isPlainObject(code) ? code : null,
	}[status];
	msg += extraMsg ? `${msg ? ': ' : ''}${_.isFunction(extraMsg) ? extraMsg(response) : extraMsg}` : '';
	Utils.noticeError(nc || document.body, msg, no);
	if (self.$events.error && event) self.fireEvent('error', [self.xhr, msg]);
};

window._Ajax = new Class({
	Extends: Request,
	options: {
		urlEncoded: false,
		headers: {
			'Content-type': 'application/json',
			...window.pageToken && { page: window.pageToken },
			...window.csrfToken && { 'csrf-token': window.csrfToken },
		},
		link: 'cancel',
		isHandleError: true,
		//successMsg: '',
		//errMsg: '',
		//errCode: {...},
		//noticeContainer: element...
		//noticeOptions: {...}
	},

	initialize: function(options){
		if (options?.url && options.url.charAt(0) === '/') options.url = options.url.substring(1);
		this.parent(options);
		Object.append(this.headers, {
			'Accept': 'application/json',
			'X-Request': 'JSON'
		});
	},

	success: function (text, xml) {
		// mootools 預設會把 <script></script> 的區段移除。
		// this.onSuccess(this.processScripts(text), xml);
		this.onSuccess(text, xml);
		this.resolve({ text: text, xml: xml });
	},

	onSuccess: function(data){
		let json;
		try {
			json = data ? JSON.parse(data) : null;
		} catch (error) {
			json = null;
		}
		this.fireEvent('complete');
		this.fireEvent('success', [json, this.xhr]);
		const {successMsg, noticeContainer:nc, noticeOptions:no} = this.options;
		const msg = _.isFunction(successMsg) ? successMsg(data) : successMsg;
		if(msg) Utils.noticeSuccess(nc || document.body, msg, no);
	},

	onFailure: function () {
		this.fireEvent('complete');
		if(this.options.isHandleError){
			let response = {};
			try{ response = JSON.parse(this.xhr.response); }
			catch(err){}
			_defaultErrorHandler(this, response);
		} else {
			let response = {};
			try{ response = JSON.parse(this.xhr.response); }
			catch(err){}
			if(this.$events.error) this.fireEvent('error', [this.xhr, () => _defaultErrorHandler(this, response, false)]);
		}
		_appErrorHandler(this.xhr.status, this.xhr);
	},

	send: function(options){
		options = {
			... this.options,
			... options,
		};
		const isPost = (options && options.method) === 'post';
		if(!isPost) return this.parent(options);

		if(options && _.isObject(options.data))
			options.data = JSON.stringify(options.data);
		return this.parent(options);
	}
});

const contentDisposition = require('content-disposition');
window._BlobAjax = new Class({
	Extends: _Ajax,
	options: {
		headers: {
			'Content-type': 'application/json',
			...window.pageToken && { page: window.pageToken },
			...window.csrfToken && { 'csrf-token': window.csrfToken },
		},
		secure: true,
		handleDownload: true,
	},

	initialize: function (options) {
		this.parent(options);
		this.xhr.responseType = 'blob';
	},

	onStateChange: function () {
		const xhr = this.xhr;
		if (xhr.readyState != 4 || !this.running) return;
		this.running = false;
		this.status = 0;
		Function.attempt(function () {
			const status = xhr.status;
			this.status = (status == 1223) ? 204 : status;
		}.bind(this));
		//xhr.onreadystatechange = empty;
		//if (progressSupport) xhr.onprogress = xhr.onloadstart = empty;
		if (this.timer) {
			clearTimeout(this.timer);
			delete this.timer;
		}
		if (this.options.isSuccess.call(this, this.status)) {
			this.fireEvent('complete');
			this.success(xhr.response, xhr);
		} else this.failure();
	},

	success: function(blob, xhr) {
		if (!this.options.handleDownload) return this.fireEvent('success', [blob, xhr]);
		let fileName = this.options.fileName;
		if (!fileName) {
			const header = xhr.getResponseHeader('Content-Disposition');
			if (header) {
				const cd = contentDisposition.parse(header);
				fileName = cd ? decodeURIComponent(cd.parameters.filename) : null;
			}
		}
		// see also: https://www.npmjs.com/package/file-saver
		// see also: https://www.npmjs.com/package/streamsaver
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.target = '_blank';
		a.href = url;
		if (fileName) a.download = fileName; // HTML5
		a.dispatchEvent(new MouseEvent('click'));
		URL.revokeObjectURL(url);
		this.fireEvent('success', [xhr]);
	},

	onFailure: function () {
		this.fireEvent('complete');
		if(this.options.isHandleError) {
			const fr = new FileReader();
			fr.addEventListener('loadend', evt => {
				this.fireEvent('complete');
				let response = {};
				try { response = JSON.parse(evt.target.result); }
				catch(err){}
				_defaultErrorHandler(this, response);
			});
			fr.readAsText(this.xhr.response, 'UTF-8');
		} else {
			if(this.$events.error) this.fireEvent('error', this.xhr);
		}
		_appErrorHandler(this.xhr.status, this.xhr);
	},
});

function blob2text(blob) {
	const xhr = new XMLHttpRequest(), ourl = URL.createObjectURL(blob);
	xhr.open('GET', ourl, false);
	xhr.send();
	URL.revokeObjectURL(ourl);
	return xhr.responseText;
}

self._DownloadAjax = new Class({
	Extends: _BlobAjax,
	success: function (blob, xhr) {
		if (xhr.status == 201) {
			try {
				blob = JSON.parse(blob2text(blob));
			} catch (ex) {
				blob = xhr.response;
			}
			return this.fireEvent('success', [blob, xhr]);
		}
		if (!this.options.handleDownload) return this.fireEvent('success', [blob, xhr]);

		let fileName = this.options.fileName;
		if (!fileName) {
			const header = xhr.getResponseHeader('Content-Disposition');
			if (header) {
				const cd = contentDisposition.parse(header);
				fileName = cd ? decodeURIComponent(cd.parameters.filename) : null;
			}
		}
		// see also: https://www.npmjs.com/package/file-saver
		// see also: https://www.npmjs.com/package/streamsaver
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.target = '_blank';
		a.href = url;
		if (fileName) a.download = fileName; // HTML5
		a.dispatchEvent(new MouseEvent('click'));
		URL.revokeObjectURL(url);
		this.fireEvent('success', [blob, xhr]); // !!注意這裡統一回傳 blob!!
	},
});

const progressSupport = ('onprogress' in new Browser.Request);
window._UploadAjax = new Class({
	Extends: Request,
	// Extends: window._Ajax,
	options: {
		// method: 'post',
		// link: 'cancel',
		urlEncoded: false,
		// emulation: false,
		isHandleError: true,
		headers: {
			...window.pageToken && { page: window.pageToken },
			...window.csrfToken && { 'csrf-token': window.csrfToken },
		},
	},

	initialize: function(options){
		if (options?.url && options.url.charAt(0) === '/') options.url = options.url.substring(1);
		// this.xhr = new Browser.Request();
		// this.setOptions(options);
		// this.headers = this.options.headers;
		this.parent(options); // TODO 就是上面三行，還是就抽出來算了？
		// forced-overwrite
		this.options.method = 'post';
		this.options.link = 'cancel';
		this.options.emulation = false;

		// this.xhr = new Browser.Request();
		this.formData = new FormData();
		// this.setOptions(options);
		// TODO 注意，上層有增加 header
		// this.headers = this.options.headers;
	},

	append: function(key, value){
		this.formData.append(key, value);
		return this;
	},

	reset: function(){
		this.formData = new FormData();
		return this;
	},

	send: function (options) {
		if (!this.check(options)) return this;

		this.options.isSuccess = this.options.isSuccess || this.isSuccess;
		this.running = true;

		const old = this.options;
		options = Object.append({ url: old.url }, options);
		const url = (options.url.charAt(0) === '/') ? options.url.substring(1) : options.url;

		const xhr = this.xhr;
		if (progressSupport) {
			xhr.onloadstart = this.loadstart.bind(this);
			xhr.onprogress = this.progress.bind(this);
			// xhr.upload.onprogress = this.progress.bind(this); // TODO mootools 沒這行
		}

		// @see https://developer.mozilla.org/ja/docs/Web/API/XMLHttpRequest/open
		xhr.open('POST', url, true, null, null);

		xhr.onreadystatechange = this.onStateChange.bind(this);

		Object.each(this.headers, function (value, key) {
			try {
				xhr.setRequestHeader(key, value);
			} catch (e) {
				this.fireEvent('exception', [key, value]);
				// this.reject({reason: 'exception', xhr: xhr, exception: e}); // 沒有要實作 Thenable
			}
		}, this);

		this.fireEvent('request');
		xhr.send(this.formData);

		// if(!this.options.async) this.onStateChange();// async 恆為 true
		if (this.options.timeout) this.timer = this.timeout.delay(this.options.timeout, this);
		return this;
	},

	onSuccess: function(data){
		let json;
		try {
			json = data ? JSON.parse(data) : null;
		} catch (error) {
			json = null;
		}
		this.fireEvent('complete');
		this.fireEvent('success', [json, this.xhr]);
		const {successMsg, noticeContainer:nc, noticeOptions:no} = this.options;
		const msg = _.isFunction(successMsg) ? successMsg(data) : successMsg;
		if(msg) Utils.noticeSuccess(nc || document.body, msg, no);
	},

	onFailure: function () {
		this.fireEvent('complete');
		if(this.options.isHandleError){
			let response = {};
			try{ response = JSON.parse(this.xhr.response); }
			catch(err){}
			_defaultErrorHandler(this, response);
		} else {
			if(this.$events.error) this.fireEvent('error', this.xhr);
		}
		_appErrorHandler(this.xhr.status, this.xhr);
	},
});
